import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { candidatesApi, jobsApi, tagsApi, formatApiError } from '../../lib/api';
import { handleAnalyzeError } from '../../lib/quotaGate';
import { hasScore } from '../../lib/score';
import { Button } from '../../components/ui/button';
import {
    ArrowLeft, Mail, Phone, FileText, Briefcase, GraduationCap,
    Brain, Loader2, Download, Target, TrendingUp, Clock, Building,
    Tag, Sparkles, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import TagInput from '../../components/TagInput';
import OcrBadge from '../../components/OcrBadge';
import { toast } from 'sonner';

export default function CandidateDetailPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [candidate, setCandidate] = useState(null);
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [tagRegistry, setTagRegistry] = useState([]);

    useEffect(() => {
        loadCandidate();
        loadTagRegistry();
    }, [id]);

    const loadTagRegistry = async () => {
        try {
            const res = await tagsApi.list();
            setTagRegistry(res.data.tags || []);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        loadCandidate();
    }, [id]);

    const loadCandidate = async () => {
        try {
            const response = await candidatesApi.getById(id);
            setCandidate(response.data);

            if (response.data.matched_job_id) {
                try {
                    const jobRes = await jobsApi.getById(response.data.matched_job_id);
                    setJob(jobRes.data);
                } catch {
                    // Job was deleted - candidate is now unassigned, don't block the page
                    setJob(null);
                }
            }
        } catch (error) {
            toast.error('Candidat non trouvé');
            navigate('/dashboard/candidates');
        } finally {
            setLoading(false);
        }
    };

    const handleAnalyze = async () => {
        if (analyzing) return;
        setAnalyzing(true);
        try {
            await candidatesApi.analyze(id);
            toast.success(t('candidates.toast_analysis_done'));
            loadCandidate();
        } catch (error) {
            const status = error?.response?.status;
            if (status === 409) {
                toast.error(t('candidates.toast_already_processing'));
                loadCandidate();
            } else {
                // Quota errors → role-aware dialog (overage/upgrade) ; rest → toast.
                handleAnalyzeError(error, () => toast.error(formatApiError(error)));
            }
        } finally {
            setAnalyzing(false);
        }
    };

    const handleDownloadCv = async () => {
        try {
            const response = await candidatesApi.downloadCv(id);
            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = candidate.cv_filename;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Erreur lors du téléchargement');
        }
    };

    const [exportingPdf, setExportingPdf] = useState(false);

    const handleExportPdf = async () => {
        if (exportingPdf) return;
        setExportingPdf(true);
        try {
            const response = await candidatesApi.exportPdf(id);
            // Filename comes back via Content-Disposition; parse it defensively
            // and fall back to a sensible default.
            let filename = `candidate_${(candidate?.name || 'profile').replace(/\s+/g, '_')}.pdf`;
            const cd = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
            if (cd) {
                const match = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
                if (match) filename = match[1];
            }
            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast.success(t('candidates.pdf_export_success'));
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setExportingPdf(false);
        }
    };

    const handleSetTags = async (nextTags) => {
        try {
            const previous = candidate?.tags || [];
            // Optimistic update
            setCandidate((c) => (c ? { ...c, tags: nextTags } : c));
            await candidatesApi.updateTags(id, 'set', nextTags);
            loadTagRegistry();
            if (nextTags.length > previous.length) toast.success(t('tags.tag_added'));
            else if (nextTags.length < previous.length) toast.success(t('tags.tag_removed'));
        } catch (error) {
            toast.error(formatApiError(error));
            loadCandidate();
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return '#10B981';
        if (score >= 60) return '#3B82F6';
        if (score >= 40) return '#F59E0B';
        return '#EF4444';
    };

    const getScoreLabel = (score) => {
        if (score >= 80) return t('candidates.detail_score_excellent');
        if (score >= 60) return t('candidates.detail_score_good');
        if (score >= 40) return t('candidates.detail_score_average');
        return t('candidates.detail_score_weak');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
            </div>
        );
    }

    if (!candidate) return null;

    return (
        <div className="space-y-6" data-testid="candidate-detail-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <button 
                        onClick={() => navigate('/dashboard/candidates')}
                        className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors flex-shrink-0"
                        data-testid="back-to-candidates-btn"
                    >
                        <ArrowLeft className="w-5 h-5 text-[#4B5563]" />
                    </button>
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="text-xl sm:text-2xl font-bold text-[#0A0A0A] font-['Outfit'] truncate">{candidate.name}</h1>
                            <OcrBadge active={candidate.is_ocr} />
                        </div>
                        <p className="text-[#4B5563] truncate">{candidate.cv_filename}</p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                    {candidate.status !== 'analyzed' && (
                        <Button
                            onClick={handleAnalyze}
                            disabled={analyzing || candidate.analysis_status === 'processing'}
                            className="bg-[#002FA7] hover:bg-[#002480] text-white flex-1 sm:flex-initial"
                            data-testid="analyze-candidate-btn"
                        >
                            {(analyzing || candidate.analysis_status === 'processing') ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {t('candidates.detail_analyzing')}
                                </>
                            ) : (
                                <>
                                    <Brain className="w-4 h-4 mr-2" />
                                    {t('candidates.detail_analyze')}
                                </>
                            )}
                        </Button>
                    )}
                    <Button variant="outline" onClick={handleDownloadCv} className="flex-1 sm:flex-initial">
                        <Download className="w-4 h-4 mr-2" />
                        CV
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleExportPdf}
                        disabled={exportingPdf}
                        data-testid="export-pdf-btn"
                        className="flex-1 sm:flex-initial"
                    >
                        {exportingPdf ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <FileText className="w-4 h-4 mr-2" />
                        )}
                        {t('candidates.export_pdf')}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main info */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Contact */}
                    <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                            {t('candidates.detail_contact_info')}
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {candidate.email && (
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-[#DBEAFE] rounded-lg flex items-center justify-center">
                                        <Mail className="w-5 h-5 text-[#002FA7]" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-[#9CA3AF]">{t('settings.email')}</p>
                                        <p className="text-[#0A0A0A]">{candidate.email}</p>
                                    </div>
                                </div>
                            )}
                            {candidate.phone && (
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-[#D1FAE5] rounded-lg flex items-center justify-center">
                                        <Phone className="w-5 h-5 text-[#10B981]" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-[#9CA3AF]">{t('candidates.detail_phone')}</p>
                                        <p className="text-[#0A0A0A]">{candidate.phone}</p>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#FEF3C7] rounded-lg flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-[#F59E0B]" />
                                </div>
                                <div>
                                    <p className="text-sm text-[#9CA3AF]">{t('candidates.detail_experience')}</p>
                                    <p className="text-[#0A0A0A]">{t('candidates.detail_experience_years', { count: candidate.experience_years || 0 })}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Skills */}
                    <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                            {t('candidates.detail_skills')}
                        </h2>
                        {candidate.skills?.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {candidate.skills.map((skill, index) => (
                                    <span 
                                        key={index}
                                        className="px-3 py-1.5 bg-[#F3F4F6] text-[#0A0A0A] rounded-lg text-sm"
                                    >
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-[#9CA3AF]">{t('candidates.detail_no_skills')}</p>
                        )}
                    </div>

                    {/* Work history */}
                    <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                            {t('candidates.detail_work_history')}
                        </h2>
                        {candidate.work_history?.length > 0 ? (
                            <div className="space-y-4">
                                {candidate.work_history.map((work, index) => (
                                    <div key={index} className="flex gap-4 pb-4 border-b border-[#E5E7EB] last:border-0 last:pb-0">
                                        <div className="w-10 h-10 bg-[#F3F4F6] rounded-lg flex items-center justify-center flex-shrink-0">
                                            <Building className="w-5 h-5 text-[#4B5563]" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-[#0A0A0A]">{work.title}</h3>
                                            <p className="text-[#4B5563]">{work.company}</p>
                                            {work.duration && (
                                                <p className="text-sm text-[#9CA3AF] mt-1">{work.duration}</p>
                                            )}
                                            {work.description && (
                                                <p className="text-sm text-[#4B5563] mt-2">{work.description}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-[#9CA3AF]">{t('candidates.detail_no_work')}</p>
                        )}
                    </div>

                    {/* Education */}
                    <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                            {t('candidates.detail_education')}
                        </h2>
                        {candidate.education?.length > 0 ? (
                            <div className="space-y-4">
                                {candidate.education.map((edu, index) => (
                                    <div key={index} className="flex gap-4">
                                        <div className="w-10 h-10 bg-[#DBEAFE] rounded-lg flex items-center justify-center flex-shrink-0">
                                            <GraduationCap className="w-5 h-5 text-[#002FA7]" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-[#0A0A0A]">{edu.degree}</h3>
                                            <p className="text-[#4B5563]">{edu.institution}</p>
                                            {edu.year && (
                                                <p className="text-sm text-[#9CA3AF]">{edu.year}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-[#9CA3AF]">{t('candidates.detail_no_education')}</p>
                        )}
                    </div>
                </div>

                {/* Sidebar - Score */}
                <div className="space-y-6">
                    {/* Score card */}
                    <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                            {t('candidates.detail_match_score')}
                        </h2>
                        
                        {hasScore(candidate.score?.overall) ? (
                            <>
                                <div className="text-center mb-6">
                                    <div 
                                        className="w-32 h-32 mx-auto rounded-full flex items-center justify-center border-8"
                                        style={{ borderColor: getScoreColor(candidate.score.overall) }}
                                    >
                                        <div>
                                            <span className="text-4xl font-bold text-[#0A0A0A] font-['Outfit']" data-testid="detail-match-score">
                                                {candidate.score.overall}
                                            </span>
                                            <span className="text-[#9CA3AF]">/100</span>
                                        </div>
                                    </div>
                                    <p 
                                        className="mt-2 font-medium"
                                        style={{ color: getScoreColor(candidate.score.overall) }}
                                    >
                                        {getScoreLabel(candidate.score.overall)}
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-[#4B5563]">{t('candidates.detail_skills_match')}</span>
                                            <span className="font-medium">{candidate.score.skills_match}</span>
                                        </div>
                                        <Progress value={candidate.score.skills_match} className="h-2" />
                                    </div>
                                    {typeof candidate.score.semantic_match === 'number' && (
                                        <div>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="text-[#4B5563]">{t('candidates.detail_semantic_match')}</span>
                                                <span className="font-medium">{candidate.score.semantic_match}</span>
                                            </div>
                                            <Progress value={candidate.score.semantic_match} className="h-2" />
                                        </div>
                                    )}
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-[#4B5563]">{t('candidates.detail_experience_match')}</span>
                                            <span className="font-medium">{candidate.score.experience_match}</span>
                                        </div>
                                        <Progress value={candidate.score.experience_match} className="h-2" />
                                    </div>
                                    {typeof candidate.score.trajectory_match === 'number' && (
                                        <div>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="text-[#4B5563]">{t('candidates.detail_trajectory_match')}</span>
                                                <span className="font-medium">{candidate.score.trajectory_match}</span>
                                            </div>
                                            <Progress value={candidate.score.trajectory_match} className="h-2" />
                                        </div>
                                    )}
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-[#4B5563]">{t('candidates.detail_job_fit')}</span>
                                            <span className="font-medium">{candidate.score.job_fit}</span>
                                        </div>
                                        <Progress value={candidate.score.job_fit} className="h-2" />
                                    </div>
                                </div>

                                {/* Critical-skills warning */}
                                {candidate.score.breakdown?.critical_missing?.length > 0 && (
                                    <div className="mt-4 p-3 bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-lg" data-testid="critical-missing-banner">
                                        <p className="text-xs font-semibold text-[#92400E]">
                                            {t('candidates.detail_critical_missing', { count: candidate.score.breakdown.critical_missing.length })}
                                        </p>
                                        <p className="text-xs text-[#78350F] mt-1">
                                            {candidate.score.breakdown.critical_missing.join(' · ')}
                                        </p>
                                        {candidate.score.critical_penalty > 0 && (
                                            <p className="text-[10px] text-[#78350F] mt-1">
                                                {t('candidates.detail_penalty_applied', { points: candidate.score.critical_penalty })}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {candidate.score.explanation && (
                                    <div className="mt-6 p-4 bg-[#F9FAFB] rounded-lg">
                                        <p className="text-sm text-[#4B5563]">
                                            <strong>{t('candidates.detail_ai_analysis')}:</strong> {candidate.score.explanation}
                                        </p>
                                    </div>
                                )}

                                {candidate.score.analysis_v2 && (
                                    <DeepAnalysisSection v2={candidate.score.analysis_v2} />
                                )}
                            </>
                        ) : (
                            <div className="text-center py-8">
                                <Target className="w-12 h-12 mx-auto text-[#9CA3AF] mb-2" />
                                <p className="text-[#9CA3AF]">{t('candidates.detail_no_score')}</p>
                                {candidate.status === 'pending' && (
                                    <Button 
                                        onClick={handleAnalyze}
                                        disabled={analyzing}
                                        className="mt-4 bg-[#002FA7] hover:bg-[#002480] text-white"
                                    >
                                        {analyzing ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Brain className="w-4 h-4 mr-2" />
                                        )}
                                        {t('candidates.detail_analyze')}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Matched job */}
                    {job && (
                        <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                            <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                                {t('candidates.detail_associated_job')}
                            </h2>
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-[#DBEAFE] rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Briefcase className="w-5 h-5 text-[#002FA7]" />
                                </div>
                                <div>
                                    <h3 className="font-medium text-[#0A0A0A]">{job.title}</h3>
                                    <p className="text-sm text-[#9CA3AF]">{job.location || t('common.not_specified')}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tags */}
                    <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]" data-testid="candidate-tags-section">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4 flex items-center gap-2">
                            <Tag className="w-5 h-5 text-[#002FA7]" />
                            {t('candidates.detail_tags')}
                        </h2>
                        <TagInput
                            value={candidate.tags || []}
                            registry={tagRegistry}
                            onChange={handleSetTags}
                            data-testid="candidate-detail-tags"
                        />
                        {(candidate.tags || []).length === 0 && (
                            <p className="text-sm text-[#9CA3AF] mt-2">{t('candidates.detail_no_tags')}</p>
                        )}
                    </div>

                    {/* Status */}
                    <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                            {t('candidates.detail_status')}
                        </h2>
                        <div className={`px-4 py-2 rounded-lg text-center font-medium ${
                            candidate.status === 'analyzed' ? 'bg-[#D1FAE5] text-[#059669]' :
                            candidate.status === 'pending' ? 'bg-[#FEF3C7] text-[#D97706]' :
                            'bg-[#FEE2E2] text-[#DC2626]'
                        }`}>
                            {candidate.status === 'analyzed' ? t('candidates.detail_status_analyzed') :
                             candidate.status === 'pending' ? t('candidates.detail_status_pending') : t('candidates.detail_status_failed')}
                        </div>
                        <p className="text-sm text-[#9CA3AF] mt-3 text-center">
                            {t('candidates.detail_imported_on', { date: new Date(candidate.created_at).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'fr-FR') })}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}



// Collapsible "Analyse approfondie" — surfaces the v2 scoring engine extras
// (implicit skills, transferable skills, gap analysis, detailed reasoning).
function DeepAnalysisSection({ v2 }) {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    if (!v2) return null;

    const gap = v2.semantic_gap_analysis || {};
    const renderChips = (items, accent) => (
        <div className="flex flex-wrap gap-1.5">
            {items.map((s, i) => (
                <span
                    key={`${s}-${i}`}
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${accent}`}
                >
                    {s}
                </span>
            ))}
        </div>
    );

    return (
        <div
            data-testid="deep-analysis-section"
            className="mt-4 border border-[#E5E7EB] rounded-lg overflow-hidden"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="deep-analysis-toggle"
                aria-expanded={open}
                className="w-full flex items-center justify-between p-4 bg-[#FAFAFA] hover:bg-[#F3F4F6] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]"
            >
                <span className="flex items-center gap-2 text-sm font-semibold text-[#0A0A0A]">
                    <Sparkles className="w-4 h-4 text-[#7C3AED]" />
                    {t('candidates.deep_analysis_title')}
                    {v2.atypical && (
                        <span
                            data-testid="deep-analysis-atypical-badge"
                            className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#EDE9FE] text-[#6D28D9]"
                        >
                            {t('candidates.deep_analysis_atypical')}
                        </span>
                    )}
                </span>
                {open ? <ChevronUp className="w-4 h-4 text-[#6B7280]" /> : <ChevronDown className="w-4 h-4 text-[#6B7280]" />}
            </button>
            {open && (
                <div className="p-4 space-y-4 text-sm">
                    {v2.detailed_reasoning && (
                        <div className="p-3 bg-[#EDE9FE]/30 border border-[#EDE9FE] rounded">
                            <p className="text-xs font-semibold text-[#6D28D9] mb-1 uppercase">
                                {t('candidates.deep_analysis_reasoning')}
                            </p>
                            <p className="text-[#0A0A0A] leading-relaxed">{v2.detailed_reasoning}</p>
                        </div>
                    )}

                    {(v2.explicit_skills_detected?.length > 0) && (
                        <div>
                            <p className="text-xs font-semibold text-[#4B5563] mb-2 uppercase tracking-wide">
                                {t('candidates.deep_analysis_explicit')}
                            </p>
                            {renderChips(v2.explicit_skills_detected, 'bg-[#D1FAE5] text-[#065F46]')}
                        </div>
                    )}
                    {(v2.implicit_skills_detected?.length > 0) && (
                        <div data-testid="deep-analysis-implicit">
                            <p className="text-xs font-semibold text-[#4B5563] mb-2 uppercase tracking-wide">
                                {t('candidates.deep_analysis_implicit')}
                            </p>
                            {renderChips(v2.implicit_skills_detected, 'bg-[#DBEAFE] text-[#1E40AF]')}
                        </div>
                    )}
                    {(v2.transferable_skills?.length > 0) && (
                        <div data-testid="deep-analysis-transferable">
                            <p className="text-xs font-semibold text-[#4B5563] mb-2 uppercase tracking-wide">
                                {t('candidates.deep_analysis_transferable')}
                            </p>
                            {renderChips(v2.transferable_skills, 'bg-[#FCE7F3] text-[#9D174D]')}
                        </div>
                    )}
                    {(v2.missing_skills?.length > 0) && (
                        <div>
                            <p className="text-xs font-semibold text-[#4B5563] mb-2 uppercase tracking-wide">
                                {t('candidates.deep_analysis_missing')}
                            </p>
                            {renderChips(v2.missing_skills, 'bg-[#FEE2E2] text-[#991B1B]')}
                        </div>
                    )}

                    {/* Gap analysis */}
                    {typeof gap.gap === 'number' && (
                        <div
                            data-testid="deep-analysis-gap"
                            className={`p-3 rounded border ${gap.compensated ? 'border-[#FCD34D] bg-[#FFFBEB]' : 'border-[#E5E7EB] bg-[#F9FAFB]'}`}
                        >
                            <p className="text-xs font-semibold uppercase tracking-wide text-[#4B5563] mb-1">
                                {t('candidates.deep_analysis_gap_title')}
                            </p>
                            <p className="text-[#0A0A0A]">
                                {t('candidates.deep_analysis_gap_body', {
                                    sem: gap.semantic_score,
                                    raw: gap.skill_score_raw,
                                    adjusted: gap.skill_score_adjusted,
                                    gap: gap.gap,
                                })}
                            </p>
                            {gap.compensated && (
                                <p className="mt-1 text-xs text-[#92400E]">
                                    {t('candidates.deep_analysis_gap_compensated')}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Strengths / weaknesses */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {(v2.strengths?.length > 0) && (
                            <div>
                                <p className="text-xs font-semibold text-[#065F46] mb-1 flex items-center gap-1">
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    {t('candidates.deep_analysis_strengths')}
                                </p>
                                <ul className="space-y-1 text-[#0A0A0A]">
                                    {v2.strengths.map((s, i) => (
                                        <li key={i} className="text-xs">· {s}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {(v2.weaknesses?.length > 0) && (
                            <div>
                                <p className="text-xs font-semibold text-[#92400E] mb-1 flex items-center gap-1">
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    {t('candidates.deep_analysis_weaknesses')}
                                </p>
                                <ul className="space-y-1 text-[#0A0A0A]">
                                    {v2.weaknesses.map((w, i) => (
                                        <li key={i} className="text-xs">· {w}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {v2.model_used && (
                        <p className="text-[10px] text-[#9CA3AF] italic">
                            {t('candidates.deep_analysis_model', { model: v2.model_used })}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
